export const tocItems = [
  { id: 'what-is', label: 'What is AI workflow automation?' },
  { id: 'top-companies', label: 'Top 5 companies in England' },
  { id: 'comparison', label: 'Comparison table' },
  { id: 'use-cases', label: 'Business use cases' },
  { id: 'ai-agents', label: 'AI agents' },
  { id: 'customer-support', label: 'Customer support & chatbots' },
  { id: 'governance', label: 'Governance & security' },
  { id: 'custom-ai', label: 'Custom AI development' },
  { id: 'europe', label: 'Europe & Germany' },
  { id: 'selection', label: 'Choosing a provider' },
  { id: 'roi', label: 'ROI metrics' },
  { id: 'implementation', label: '10-step implementation' },
  { id: 'faq', label: 'FAQs' },
];

export const navItems = [
  { href: '#top-companies', label: 'Companies' },
  { href: '#comparison', label: 'Compare' },
  { href: '#implementation', label: 'Process' },
  { href: '#faq', label: 'FAQs' },
];

export const useCases = [
  {
    area: 'Finance',
    examples: 'Invoice capture and matching, expense review, month-end reconciliation, payment chasing.',
  },
  {
    area: 'Operations',
    examples: 'Order processing, supplier communication, scheduling, stock and delivery exceptions.',
  },
  {
    area: 'Sales & marketing',
    examples: 'Lead enrichment and routing, CRM hygiene, proposal drafting, follow-up sequences.',
  },
  {
    area: 'HR & people',
    examples: 'Onboarding checklists, policy questions, document verification, leave requests.',
  },
  {
    area: 'IT & service management',
    examples: 'Ticket triage, access requests, incident summaries, knowledge-base updates.',
  },
  {
    area: 'Legal & compliance',
    examples: 'Contract intake, clause extraction, policy checks, audit evidence collection.',
  },
];

export const selectionCriteria = [
  {
    title: 'Discovery before tooling',
    text: 'The provider should understand your process and baseline before recommending a platform or model.',
  },
  {
    title: 'Integration depth',
    text: 'Ask how they connect to your CRM, ERP, helpdesk and data sources, and how they handle systems without clean APIs.',
  },
  {
    title: 'Human-in-the-loop design',
    text: 'Approval points, escalation rules and override controls should be designed in, not added after an incident.',
  },
  {
    title: 'Security and compliance',
    text: 'Expect clear answers on data handling, hosting region, access control, logging and UK GDPR obligations.',
  },
  {
    title: 'A scoped proof of concept',
    text: 'A good POC has agreed success criteria, a fixed scope and a clear decision point at the end.',
  },
  {
    title: 'Monitoring after launch',
    text: 'Workflows drift as data and processes change. Ask who watches accuracy and exceptions once you are live.',
  },
  {
    title: 'Ownership and portability',
    text: 'Clarify who owns the code, prompts, workflows and evaluation data if the engagement ends.',
  },
  {
    title: 'Transparent commercials',
    text: 'Understand build costs, licences, model usage and support fees upfront so ROI calculations are honest.',
  },
];

export const roiMetrics = [
  { metric: 'Cycle time', measures: 'Time from request to completion', how: 'Timestamp start and end events in the workflow' },
  { metric: 'Hours returned', measures: 'Manual effort removed from the team', how: 'Time-and-motion baseline vs. post-launch sampling' },
  { metric: 'Cost per transaction', measures: 'Fully loaded cost of each processed item', how: 'Labour, licences and model usage ÷ volume' },
  { metric: 'Error and rework rate', measures: 'Items corrected after processing', how: 'QA sampling and downstream correction logs' },
  { metric: 'Exception rate', measures: 'Share of cases escalated to a person', how: 'Workflow logs of human handoffs' },
  { metric: 'Resolution quality', measures: 'Customer or internal satisfaction with outcomes', how: 'CSAT, reopen rates and reviewer scores' },
  { metric: 'Adoption', measures: 'How much work actually runs through the workflow', how: 'Automated volume ÷ total eligible volume' },
];

export const implementationSteps = [
  { title: 'Define business outcomes', text: 'Agree the result you want, such as faster turnaround or fewer errors, and who owns it.' },
  { title: 'Map and prioritise processes', text: 'Document how work flows today and rank candidates by volume, value and risk.' },
  { title: 'Assess data and system readiness', text: 'Check data quality, access permissions and integration options for every system involved.' },
  { title: 'Design the target workflow', text: 'Decide which steps AI handles, which stay manual and where human approval sits.' },
  { title: 'Build a proof of concept', text: 'Prototype the highest-value path with real, representative data in a safe environment.' },
  { title: 'Evaluate against success criteria', text: 'Measure accuracy, speed and exception rates against the baseline before committing further.' },
  { title: 'Harden for production', text: 'Add error handling, security controls, logging, rate limits and fallback paths.' },
  { title: 'Integrate and roll out in phases', text: 'Launch with one team or region first, then expand as confidence grows.' },
  { title: 'Train teams and manage change', text: 'Show people how the workflow helps them, how to review outputs and how to escalate.' },
  { title: 'Monitor, measure and optimise', text: 'Track performance continuously, retrain or re-prompt where needed and automate the next process.' },
];
